import { parse, isValid } from 'date-fns';

import { ColumnType } from '@dynamika-shared/ui/Table/Table.types';
import { AuditTrialType } from '@dynamika-shared/types/auditTrial';
import { Avatar } from '@components/Avatar/Avatar';
import { CustomTooltip } from '@dynamika-shared/ui/Tooltip/CustomTooltip';
import { formatBusinessProcess } from '@restore/auditTrailsFilters/businessProcess.helpers';

const fallbackEntity = (row: AuditTrialType) => {
  const entity = row?.payloads?.find?.(p => p?.type === 'entity');
  return entity?.entityData?.value ?? entity?.entityName ?? '-';
};

const loadingSkeleton = (width: number | string) => (
  <div
    className={`h-2 ${width} animate-pulse rounded-[5px] bg-brightGray`}
  ></div>
);

const isExternalUserAction = (businessProcess?: string): boolean =>
  businessProcess?.toLowerCase().includes('external user') ?? false;

export const makeAuditTrialsTableColumns = (): ColumnType<AuditTrialType>[] => {
  return [
    {
      name: 'actionBy',
      header: () => 'Action by',
      loading: index =>
        loadingSkeleton(Number(index) % 2 ? `w-[${22}rem]` : `w-[${19}rem]`),
      getValue: row => {
        return (
          <div className='flex items-center justify-center'>
            <Avatar userName={row.actionByName} className='mr-[12px]' />
            <div className='truncate'>
              <CustomTooltip
                label={row.actionByName ?? '-'}
                className={'truncate'}
              />
              <CustomTooltip
                label={row.actionByEmail ?? '-'}
                className={'truncate text-dimGray font-normal text-sm'}
              />
            </div>
          </div>
        );
      },
      hasSorting: false,
      thClassName: 'w-[20rem] min-w-[10%] max-w-[10%]',
      cellClassName: 'truncate',
    },
    {
      name: 'dateAndTime',
      header: () => 'Date & Time',
      loading: index =>
        loadingSkeleton(Number(index) % 2 ? `w-[${20}rem]` : `w-[${17}rem]`),
      getValue: row => {
        const [from, to] = row.dateAndTime.split(' ');
        return (
          <div className='truncate'>
            <div>{from}</div>
            <div className='text-dimGray'>{to}</div>
          </div>
        );
      },
      hasSorting: true,
      thClassName: 'w-[15rem] min-w-[12%] max-w-[12%]',
    },
    {
      name: 'subjectId',
      header: () => 'Subject ID',
      loading: index =>
        loadingSkeleton(Number(index) % 2 ? `w-[${18}rem]` : `w-[${15}rem]`),
      getValue: row => {
        return (
          <div className='truncate'>
            {(row.attributes.subjectCode || row.subjectCode) ?? '-'}
          </div>
        );
      },
      hasSorting: false,
      thClassName: 'w-[12rem] min-w-[10%] max-w-[10%]',
    },
    {
      name: 'visit',
      header: () => 'Visit',
      loading: index =>
        loadingSkeleton(Number(index) % 2 ? `w-[${20}rem]` : `w-[${17}rem]`),
      getValue: row => {
        const notEmptyVisit =
          (row.attributes?.visitName) ??
          '-';
        const parsed = parse(notEmptyVisit, 'yyyy-MM-dd', new Date());
        const unschedule = isValid(parsed);
        return (
          <div className='truncate'>
            {unschedule ? (
              <>
                <div>{'Unscheduled'}</div>
                <CustomTooltip label={row.visit} className='text-dimGray' />
              </>
            ) : (
              <CustomTooltip label={notEmptyVisit} />
            )}
          </div>
        );
      },
      hasSorting: false,
      thClassName: 'w-[15rem] min-w-[10%] max-w-[10%]',
    },
    {
      name: 'businessProcess',
      header: () => 'Business Process',
      loading: index =>
        loadingSkeleton(Number(index) % 2 ? `w-[${20}rem]` : `w-[${17}rem]`),
      getValue: row => {
        return (
          <CustomTooltip
            label={formatBusinessProcess(row.businessProcess)}
            className='truncate'
          />
        );
      },
      hasSorting: false,
      thClassName: 'w-[15rem] min-w-[14%] max-w-[14%]',
    },
    {
      name: 'event',
      header: () => 'Event',
      loading: index =>
        loadingSkeleton(Number(index) % 2 ? `w-[${20}rem]` : `w-[${17}rem]`),
      getValue: row => {
        const displayValue = isExternalUserAction(row.businessProcess)
          ? row.tags || row.event
          : row.event;

        return (
          <CustomTooltip label={displayValue} className='truncate capitalize' />
        );
      },
      hasSorting: false,
      thClassName: 'w-[15rem] min-w-[10%] max-w-[10%]',
    },
    {
      name: 'actionType',
      header: () => 'Action Type',
      loading: index =>
        loadingSkeleton(Number(index) % 2 ? `w-[${20}rem]` : `w-[${17}rem]`),
      getValue: row => {
        return <div className='truncate'>{row.actionType}</div>;
      },
      hasSorting: false,
      thClassName: 'w-[12rem] min-w-[10%] max-w-[10%]',
    },
    {
      name: 'field',
      header: () => 'Field',
      loading: index =>
        loadingSkeleton(Number(index) % 2 ? `w-[${20}rem]` : `w-[${17}rem]`),
      getValue: row => {
        return (
          <CustomTooltip
            label={row.field ?? '-'}
            className='truncate capitalize'
          />
        );
      },
      hasSorting: false,
      thClassName: 'w-[13rem] min-w-[10%] max-w-[10%]',
    },
    {
      name: 'newValue',
      header: () => 'New Value',
      loading: index =>
        loadingSkeleton(Number(index) % 2 ? `w-[${20}rem]` : `w-[${17}rem]`),
      getValue: row => {
        const isValidNewValue = row.newValue && row.newValue !== '-';

        const displayValue = isExternalUserAction(row.businessProcess)
          ? fallbackEntity(row)
          : isValidNewValue
            ? row.newValue
            : fallbackEntity(row);
        return (
          <CustomTooltip label={displayValue} className='truncate capitalize' />
        );
      },
      hasSorting: false,
      thClassName: 'w-[12rem] min-w-[12%] max-w-[12%]',
    },
    {
      name: 'oldValue',
      header: () => 'Old Value',
      loading: index =>
        loadingSkeleton(Number(index) % 2 ? `w-[${20}rem]` : `w-[${17}rem]`),
      getValue: row => {
        return (
          <CustomTooltip
            label={row.oldValue || '-'}
            className='truncate capitalize'
          />
        );
      },
      hasSorting: false,
      thClassName: 'w-[12rem] min-w-[12%] max-w-[12%]',
    },
    {
      name: 'reasonForModification',
      header: () => 'Reason for Modification',
      loading: index =>
        loadingSkeleton(Number(index) % 2 ? `w-[${20}rem]` : `w-[${17}rem]`),
      getValue: row => {
        return (
          <CustomTooltip
            label={row.reasonForModification ?? '-'}
            className='truncate capitalize'
          />
        );
      },
      hasSorting: false,
      thClassName: 'w-[15rem] min-w-[10%] max-w-[10%]',
    },
  ];
};
